import { useCallback, useEffect, useMemo, useState } from 'react'
import { ServerApiClient, type TourShareLinkRow } from '../../../data/ServerApiClient'

/**
 * Mint and manage personalized share links without leaving the rail.
 *
 * A link is pinned to the published version by default, so editing the tour
 * after sending never changes what the prospect sees. The URL it produces is
 * the whole product for a seller: `/t/<token>` with the prospect's name baked
 * into the audience.
 */
export function TourShareLinksPanel({ slug }: { slug: string }) {
  const api = useMemo(() => new ServerApiClient(), [])
  const [links, setLinks] = useState<TourShareLinkRow[]>([])
  const [label, setLabel] = useState('')
  const [firstName, setFirstName] = useState('')
  const [expiresDays, setExpiresDays] = useState('14')
  const [status, setStatus] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setLinks(await api.listTourShareLinks(slug))
    } catch (err: unknown) {
      setStatus(String((err as Error)?.message || 'Failed to load links.'))
    }
  }, [api, slug])

  useEffect(() => { void load() }, [load])

  const create = useCallback(async () => {
    setBusy(true)
    setStatus(null)
    try {
      const link = await api.createTourShareLink({
        slug,
        label: label.trim() || null,
        audience: firstName.trim() ? { first_name: firstName.trim() } : {},
        expires_in_days: Number(expiresDays) > 0 ? Number(expiresDays) : null,
        pin_version: true,
      })
      const url = `${window.location.origin}/t/${link.token}`
      await navigator.clipboard?.writeText(url).catch(() => {})
      setStatus(`Copied: ${url}`)
      setLabel('')
      setFirstName('')
      await load()
    } catch (err: unknown) {
      // The most common failure is an unpublished tour — surface the server's
      // own explanation, which says exactly that.
      setStatus(String((err as Error)?.message || 'Could not create the link.'))
    } finally {
      setBusy(false)
    }
  }, [api, slug, label, firstName, expiresDays, load])

  const revoke = useCallback(async (token: string) => {
    await api.revokeTourShareLink(token)
    await load()
  }, [api, load])

  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
      <div style={{ display: 'grid', gap: 6 }}>
        <input className="fg-author__input" placeholder="Label — e.g. Meridian Air · Tom"
          value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Link label" />
        <div style={{ display: 'flex', gap: 6 }}>
          <input className="fg-author__input" placeholder="Prospect first name (optional)"
            value={firstName} onChange={(e) => setFirstName(e.target.value)} aria-label="Prospect first name" />
          <input className="fg-author__input" style={{ width: 90 }} placeholder="Days"
            value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} aria-label="Expires in days" />
        </div>
        <button className="fg-author__btn fg-author__btn--primary" onClick={() => void create()} disabled={busy}>
          {busy ? 'Creating…' : 'Create link (copies URL)'}
        </button>
        {status ? <p className="fg-author__note">{status}</p> : null}
      </div>

      {links.length ? (
        <div style={{ display: 'grid', gap: 4 }}>
          {links.map((link) => (
            <div key={link.token} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {link.revoked ? '✕ ' : ''}{link.label || link.token.slice(0, 8)} · opened {link.openedCount}×
                {link.pinnedVersion ? ` · v${link.pinnedVersion}` : ''}
              </span>
              <button
                className="fg-author__btn"
                onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/t/${link.token}`) }}
              >Copy</button>
              {!link.revoked ? (
                <button className="fg-author__btn" onClick={() => void revoke(link.token)}>Revoke</button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="fg-author__note">No links yet. Publish the tour, then mint one per prospect.</p>
      )}
    </div>
  )
}
